import type { DatePickerComponent } from 'v-calendar/dist/types/tests/unit/specs/utils';

import { useInfiniteScroll } from '@vueuse/core';
import { computed, ref, shallowRef, watch } from 'vue';

import {
  CALENDAR_MOVE_DELAY_MS,
  DESKTOP_MONTHS_TO_FETCH,
  INFINITE_SCROLL_DISTANCE_PX,
  INITIAL_CALENDAR_MONTHS,
  MOBILE_MONTHS_TO_FETCH,
} from '@/composables/booking/booking-widget.constants';
import { useDropdown } from '@/composables/useDropdown';
import { useBookingWidgetContext } from '@/types/booking-widget-context';
import { formatDateToLocalDate } from '@/utils/date';

type CalendarPage = { year: number; month: number };
type DisabledDate = Date | { start: Date; end: null };

export function useDateSelectionDropdown() {
  const context = useBookingWidgetContext();
  const isMobile = context.bookingWidget.isMobile;
  const today = new Date();
  const monthsFetched = ref(INITIAL_CALENDAR_MONTHS);
  today.setHours(0, 0, 0, 0);

  const isOpen = ref(false);
  const dropdown = ref<HTMLElement>();
  const selectedDate = ref<Date | null>(null);
  const mobileRows = ref(INITIAL_CALENDAR_MONTHS);
  const calendarRef = ref<DatePickerComponent>(null);
  const mobileScrollContainer = ref<HTMLElement | null>(null);
  const lastVisiblePage = shallowRef<CalendarPage | null>(null);
  let isExtendingMaxDate = false;

  const maxDate = ref<Date>(new Date(today.getFullYear(), today.getMonth() + INITIAL_CALENDAR_MONTHS, 1));
  const disabledDates = computed<DisabledDate[]>(() => {
    const calendarData = context.bookingWidget.calendarData.value;

    const availableDatesSet = new Set(
      calendarData
        .filter(day => day.available && day.status !== 'CLOSED' && day.status !== 'SOLD_OUT')
        .map(day => day.localDate),
    );

    const disabled: DisabledDate[] = [];
    const current = new Date(today);

    while (current <= maxDate.value) {
      const localDate = formatDateToLocalDate(current);

      if (!availableDatesSet.has(localDate)) {
        disabled.push(new Date(current));
      }

      current.setDate(current.getDate() + 1);
    }

    // Months beyond maxDate are not fetched yet: never show them as available
    disabled.push({ start: new Date(maxDate.value), end: null });

    return disabled;
  });

  const displayText = computed(() => {
    if (!selectedDate.value) {
      return '';
    }
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
    };
    return new Intl.DateTimeFormat(context.bookingWidget.currentLocale.value, options).format(selectedDate.value);
  });
  const { openDropdown, closeDropdown, handleOutsideClick } = useDropdown({
    isOpen,
    dropdownRef: dropdown,
    isMobile,
    onOpen: () => {
      setTimeout(() => {
        if (calendarRef.value && isMobile.value) {
          calendarRef.value.move(new Date());
        }
      }, CALENDAR_MOVE_DELAY_MS);
    },
    onClose: () => {
      lastVisiblePage.value = null;
    },
  });

  // Selecting a date applies it right away and fetches its sessions
  function selectDate(date: Date | null) {
    if (!date)
      return;
    selectedDate.value = date;
    context.bookingWidget.handleDateSelected(date);
    closeDropdown();
    context.bookingWidget.searchAvailabilities();
  }

  // The calendar does not emit a value update when the already selected date is clicked again:
  // close it anyway, and only fetch the sessions if they have not been fetched yet
  function handleDayClick(day: { date: Date }) {
    if (!isOpen.value || !selectedDate.value)
      return;
    if (formatDateToLocalDate(day.date) !== formatDateToLocalDate(selectedDate.value))
      return;

    if (context.bookingWidget.hasCheckedOnce.value)
      closeDropdown();
    else
      selectDate(selectedDate.value);
  }

  // maxDate is always derived from the fetched months count, so a stale or duplicated
  // response can never push it past the months actually loaded
  function setMonthsFetched(count: number) {
    const currentMonth = new Date();
    monthsFetched.value = count;
    maxDate.value = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + count, 1);
  }

  function resetCalendarPagination() {
    setMonthsFetched(INITIAL_CALENDAR_MONTHS);
    mobileRows.value = INITIAL_CALENDAR_MONTHS;
  }

  function extendMaxDate(monthsToFetch = isMobile.value ? MOBILE_MONTHS_TO_FETCH : DESKTOP_MONTHS_TO_FETCH) {
    if (context.bookingWidget.isLoadingCalendar.value || isExtendingMaxDate)
      return;

    isExtendingMaxDate = true;
    const fetchStartMonth = monthsFetched.value;

    return context.bookingWidget.loadCalendarAvailabilities(fetchStartMonth, monthsToFetch).then((isLoaded) => {
      isExtendingMaxDate = false;
      if (!isLoaded)
        return;

      setMonthsFetched(Math.max(monthsFetched.value, fetchStartMonth + monthsToFetch));
      if (isMobile.value) {
        mobileRows.value += 1;
      }
      recheckVisibleMonths();
    });
  }

  // Called on every page change of the desktop calendar, including its first render
  // (e.g. reopening on a previously selected date far from today)
  function ensureVisibleMonthsLoaded(pages: CalendarPage[]) {
    if (!pages?.length)
      return;

    const lastPage = pages[pages.length - 1];
    lastVisiblePage.value = { year: lastPage.year, month: lastPage.month };

    const lastVisibleMonth = new Date(lastPage.year, lastPage.month - 1, 1);
    if (lastVisibleMonth < maxDate.value)
      return;

    const missingMonths = (lastVisibleMonth.getFullYear() - maxDate.value.getFullYear()) * 12
      + lastVisibleMonth.getMonth() - maxDate.value.getMonth() + 1;
    extendMaxDate(Math.max(missingMonths, DESKTOP_MONTHS_TO_FETCH));
  }

  function recheckVisibleMonths() {
    if (isOpen.value && lastVisiblePage.value) {
      ensureVisibleMonthsLoaded([lastVisiblePage.value]);
    }
  }

  function retryCalendarLoad() {
    resetCalendarPagination();
    context.bookingWidget.loadCalendarAvailabilities();
  }

  // A page change during a widget-initiated load is skipped by extendMaxDate: re-check once it is over.
  // Loads started by extendMaxDate re-check themselves once maxDate is updated (this watcher runs before),
  // and failed loads are retried manually by the user.
  watch(context.bookingWidget.isLoadingCalendar, (isLoading) => {
    const hasLoadFailed = context.bookingWidget.bookingError.value !== null;
    if (!isLoading && !hasLoadFailed && !isExtendingMaxDate) {
      recheckVisibleMonths();
    }
  });

  useInfiniteScroll(
    mobileScrollContainer,
    () => {
      if (!context.bookingWidget.isLoadingCalendar.value && isMobile.value) {
        extendMaxDate();
      }
    },
    { distance: INFINITE_SCROLL_DISTANCE_PX },
  );

  context.bookingWidget.registerDateDropdownOpener(openDropdown);
  context.bookingWidget.registerCalendarPaginationResetter(resetCalendarPagination);

  return {
    isMobile,
    isOpen,
    dropdown,
    selectedDate,
    mobileRows,
    maxDate,
    calendarRef,
    mobileScrollContainer,
    disabledDates,
    displayText,
    openDropdown,
    closeDropdown,
    handleOutsideClick,
    selectDate,
    handleDayClick,
    resetCalendarPagination,
    extendMaxDate,
    ensureVisibleMonthsLoaded,
    retryCalendarLoad,
    formatDateToLocalDate,
  };
}
